import React, { Fragment } from 'react';
import { useOrderDetailModel } from './OrderDetailModel';
import { RouteComponentProps, Switch, Route, Link } from 'react-router-dom';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { NavDropdown, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { DownloadReportPanel } from 'components/DownloadReportPanel/DownloadReportPanel';
import {
  DownloadOrderReportPanelModel,
  GroupBy
} from 'components/DownloadReportPanel/DownloadReportPanelModel';
import { ReportType } from 'core/report/ReportData';
import classNames from 'classnames/bind';
import styles from './orderDetail.module.scss';
import i18n from 'i18n';
import _ from 'lodash';
import RouteWithPermission from 'components/RouteWithPermission/RouteWithPermission';
import { addOnEnabled, hasFuncs } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { ROUTE_PATH } from 'enum/RoutePath';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleUp,
  faAngleDown,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { L1Objects } from 'containers/L1Objects/L1Objects';
import { Actor, AddonFeatureManager, AuthenticationManager } from 'core';
import { L1ObjectList } from 'containers/L1Objects/L1ObjectList/L1ObjectList';
import { TitleWithBreadcrumbs } from 'components/TitleArea/TitleWithBreadcrumbs';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { DownloadOutdoorReportPanel } from 'components/DownloadReportPanel/DownloadOutdoorReportPanel';
import { shouldShowDownloadOutdoorReport } from 'components/DownloadReportPanel/outdoorReportTypes';
import { DownloadOutdoorFinalReportPanel } from 'components/DownloadReportPanel/DownloadOutdoorFinalReportPanel';
import { FinalReportType } from 'core/finalReport/FinalReport';
import { ADDONFEATURE } from 'core/agency/AddonFeature';

export const OrderDetail: React.FC<
  RouteComponentProps & {
    orderNumber: string;
    localMetaDataUpdater: (actor: Actor | null) => Promise<void>;
    authenticationManager: AuthenticationManager;
    addonFeatureManager: AddonFeatureManager;
  }
> = ({
  orderNumber,
  localMetaDataUpdater,
  authenticationManager,
  addonFeatureManager,
  match
}) => {
  const {
    loading,
    order,
    viewData,
    canSettle,
    settleTips,
    showDetail,
    canEditOrder,
    showSettleButton,
    showUnsettleButton,
    showRejectButton,
    showApproveButton,
    l1ObjectList,
    showGoVideoReportButton,
    showDownloadRtbReportModal,
    showDownloadOutdoorReportModal,
    showDownloadFinalReport,
    fetchingFinalReport,
    remainingStoredValueError,
    settleOrder,
    unsettleOrder,
    rejectOrder,
    approveOrder,
    getReportLink,
    toggleShowDetail,
    setShowDownloadRtbReportModal,
    setShowDownloadOutdoorReportModal,
    showDownloadFinalReportModal,
    setShowDownloadFinalReportModal,
    refreshOrderDetail,
    openFinalReport
  } = useOrderDetailModel(
    orderNumber,
    localMetaDataUpdater,
    authenticationManager,
    addonFeatureManager
  );

  const cssClassNames = classNames.bind(styles);

  // 只有含一般 Outdoor 或 Beacon Network 活動的訂單才有 Outdoor 報表可下載。
  // 條件與面板內的下拉選項共用同一份判定（outdoorReportTypes）。
  const showDownloadOutdoorReport =
    shouldShowDownloadOutdoorReport(l1ObjectList);

  const renderL1Objects = (props) => {
    if (!order) {
      return <LoadingIndicator />;
    }
    return (
      <L1Objects
        {...props}
        order={order}
        addonFeatureManager={addonFeatureManager}
      />
    );
  };

  const renderOrderData = (orderViewData, type) => {
    return Object.keys(orderViewData).map((key) => {
      const i18nKey = `orderDetail.labels.${key}`;
      const title = i18n.exists(i18nKey) ? i18n.t(i18nKey) : key;
      let value = orderViewData[key];
      let hint = '';
      let valueClassName = styles.fieldValue;
      if (orderViewData[key] && orderViewData[key].change) {
        value = orderViewData[key].change;
        hint = orderViewData[key].origin;
        valueClassName = `${styles.fieldValue} ${styles.changeValue}`;
      } else if (orderViewData[key] && orderViewData[key].extraInfo) {
        const lis = orderViewData[key].extraInfo.map((extraInfo) => (
          <li key={extraInfo}>{extraInfo}</li>
        ));
        return (
          <div className={styles.field} key={key}>
            <div className={styles.fieldLabel}>{title}</div>
            <div className={valueClassName}>
              {orderViewData[key].value}
              {orderViewData[key].extraInfo.length > 0 && (
                <OverlayTrigger
                  key={key}
                  placement={'right'}
                  trigger={['hover', 'focus']}
                  overlay={
                    <Tooltip id={key + '-orderExtraInfo'}>
                      <ul>{lis}</ul>
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </OverlayTrigger>
              )}
            </div>
          </div>
        );
      }

      if (type === 'performance') {
        valueClassName = `${styles.fieldValue} ${styles.performanceField}`;
      }

      return (
        <div className={styles.field} key={key}>
          <div className={styles.fieldLabel}>{title}</div>
          <div className={valueClassName}>{value}</div>
          <div className={styles.fieldHint}>{hint}</div>
        </div>
      );
    });
  };

  const renderSection = (data, type) => {
    if (!data) {
      return <></>;
    }
    return (
      <Fragment>
        <div className={styles.sepLine} />
        <div className={styles[type]}>{renderOrderData(data, type)}</div>
      </Fragment>
    );
  };

  const renderL1ObjectList = (_props) => {
    if (!order) {
      return <div />;
    }
    return (
      <L1ObjectList
        order={order}
        l1ObjectList={l1ObjectList}
        refreshList={refreshOrderDetail}
        addonFeatureManager={addonFeatureManager}
      />
    );
  };

  const renderReportDropDown = () => {
    const handleNavEvent = (e) => {
      e && e.stopPropagation();
    };

    return (
      <NavDropdown
        title={i18n.t('orderDetail.labels.report')}
        id='goReportList'
        onClick={handleNavEvent}
      >
        <NavDropdown.Item
          target='_blank'
          href={getReportLink(ReportType.PERFORMANCE)}
        >
          {i18n.t('orderDetail.labels.generalReport')}
        </NavDropdown.Item>
        <NavDropdown.Item
          target='_blank'
          href={getReportLink(ReportType.VIDEO)}
        >
          {i18n.t('orderDetail.labels.videoReport')}
        </NavDropdown.Item>
      </NavDropdown>
    );
  };

  const renderContent = (props) => {
    if (_.isEmpty(viewData)) {
      return <LoadingIndicator />;
    }

    const settleClass = cssClassNames('settleBtn', {
      disable: !canSettle,
      cursorNotAllow: !canSettle
    });
    const orderDetailClass = cssClassNames('detail', {
      show: showDetail
    });
    const onOutdoorFinalReportGenerate = (
      targetImpressions: number,
      showTop20Stores: boolean
    ) => {
      setShowDownloadFinalReportModal(false);
      openFinalReport(
        FinalReportType.OUTDOOR,
        `&targetImpressions=${targetImpressions}&showTop20Stores=${showTop20Stores}`
      );
    };
    return (
      <div className={styles.orderDetailContainer}>
        {(loading || fetchingFinalReport) && <LoadingIndicator />}
        {showDownloadRtbReportModal && (
          <DownloadReportPanel
            model={
              new DownloadOrderReportPanelModel(
                {
                  name: viewData.name,
                  orderNumber,
                  id: viewData.id
                },
                viewData.startDate,
                viewData.endDate,
                [GroupBy.L1_OBJECT, GroupBy.CAMPAIGN, GroupBy.CREATIVE],
                _.partial(setShowDownloadRtbReportModal, false),
                addonFeatureManager
              )
            }
          />
        )}
        {showDownloadOutdoorReportModal && order && (
          <DownloadOutdoorReportPanel
            order={order}
            l1ObjectList={l1ObjectList}
            addonFeatureManager={addonFeatureManager}
            onClose={_.partial(setShowDownloadOutdoorReportModal, false)}
          />
        )}
        {showDownloadFinalReportModal && (
          <DownloadOutdoorFinalReportPanel
            onGenerate={onOutdoorFinalReportGenerate}
            onClose={_.partial(setShowDownloadFinalReportModal, false)}
          />
        )}
        <TitleWithBreadcrumbs
          title={i18n.t('orderDetail.labels.title')}
          routes={[
            {
              path: '/orders',
              breadcrumb: i18n.t('orderDetail.labels.title')
            },
            {
              path: '/orders/:orderNumber',
              breadcrumb: DynamicBreadcrumb,
              props: {
                label: _.get(order, 'projectName'),
                matchParam: 'orderNumber'
              }
            }
          ]}
        />
        <div className={styles.titleBottomLine} />
        <div className={styles.orderDetail}>
          <div className={styles.topArea}>
            <div className={styles.orderName} onClick={toggleShowDetail}>
              <FontAwesomeIcon icon={showDetail ? faAngleDown : faAngleUp} />
              <div>
                {i18n.t('orderDetail.labels.name', {
                  name: viewData.name
                })}
              </div>
            </div>
            <div className={styles.buttonArea}>
              {showApproveButton && (
                <PermissionChecker
                  permissionAware={hasFuncs(Permission.ORDER_APPROVE)}
                >
                  {remainingStoredValueError ? (
                    <OverlayTrigger
                      placement={'bottom'}
                      trigger={['hover', 'focus']}
                      overlay={
                        <Tooltip id={'remainingStoredValueError'}>
                          {remainingStoredValueError}
                        </Tooltip>
                      }
                    >
                      <div
                        className={`${styles.approveBtn} ${styles.disabled}`}
                      >
                        {i18n.t('orderDetail.labels.approve')}
                      </div>
                    </OverlayTrigger>
                  ) : (
                    <div className={styles.approveBtn} onClick={approveOrder}>
                      {i18n.t('orderDetail.labels.approve')}
                    </div>
                  )}
                </PermissionChecker>
              )}
              {showRejectButton && (
                <PermissionChecker
                  permissionAware={hasFuncs(Permission.ORDER_APPROVE)}
                >
                  <div className={styles.rejectBtn} onClick={rejectOrder}>
                    {i18n.t('orderDetail.labels.reject')}
                  </div>
                </PermissionChecker>
              )}
              {canEditOrder && (
                <Link to={`${orderNumber}/edit`}>
                  {i18n.t('orderDetail.labels.editOrder')}
                </Link>
              )}
              {showSettleButton && (
                <PermissionChecker
                  permissionAware={hasFuncs(Permission.ORDER_WRITE)}
                >
                  <div onClick={settleOrder} className={settleClass}>
                    {i18n.t('orderDetail.buttons.settle')}
                    <span>{settleTips}</span>
                  </div>
                </PermissionChecker>
              )}
              {showUnsettleButton && (
                <PermissionChecker
                  permissionAware={hasFuncs(Permission.ORDER_WRITE)}
                >
                  <div onClick={unsettleOrder} className={styles.unsettleBtn}>
                    {i18n.t('orderDetail.buttons.unsettle')}
                  </div>
                </PermissionChecker>
              )}
              {showGoVideoReportButton ? (
                renderReportDropDown()
              ) : (
                <Link
                  to={getReportLink(ReportType.PERFORMANCE)}
                  target='_blank'
                >
                  {i18n.t('orderDetail.labels.report')}
                </Link>
              )}
              <div onClick={_.partial(setShowDownloadRtbReportModal, true)}>
                {i18n.t('orderDetail.labels.downloadRtbReport')}
              </div>
              {showDownloadOutdoorReport && (
                <div
                  onClick={_.partial(setShowDownloadOutdoorReportModal, true)}
                >
                  {i18n.t('orderDetail.labels.downloadOutdoorReport')}
                </div>
              )}
              {showDownloadFinalReport && (
                <div
                  onClick={_.partial(openFinalReport, FinalReportType.RTB, '')}
                >
                  {i18n.t('orderDetails.buttons.downloadFinalReport')}
                </div>
              )}
              <PermissionChecker
                permissionAware={addOnEnabled(
                  ADDONFEATURE.REPORT.REPORT_OUTDOOR_FINALREPORT
                )}
              >
                <div onClick={_.partial(setShowDownloadFinalReportModal, true)}>
                  {i18n.t('orderDetails.buttons.downloadOutdoorFinalReport')}
                </div>
              </PermissionChecker>
            </div>
          </div>
          <div className={orderDetailClass}>
            {renderSection(viewData.basic, 'basic')}
            {renderSection(viewData.performance, 'performance')}
          </div>
        </div>
        <div className={styles.other}>{renderL1ObjectList(props)}</div>
      </div>
    );
  };

  if (!viewData) {
    return <LoadingIndicator />;
  }

  return (
    <Switch>
      <RouteWithPermission
        permissionAware={hasFuncs(Permission.CAMPAIGN_READ).or(
          hasFuncs(Permission.CAMPAIGN_WRITE)
        )}
        redirectPath={`${match.url}/campaign-groups/${ROUTE_PATH.ERROR403}`}
        path={`${match.url}/campaign-groups`}
        render={renderL1Objects}
      />
      <Route render={renderContent} />
    </Switch>
  );
};
